import { c as _c } from "react/compiler-runtime";
import { CircleSpinnerInline, IconButton, MultiObjectSelector, colors, useFuzzy } from '@newfront-insurance/core-ui';
import { LineOfCoverageStageFilter } from '@newfront-insurance/coverage-api';
import type { CarrierQuote } from '@newfront-insurance/quoting-api';
import { useRef, Suspense, type JSX } from 'react';
import { useSuspenseCarrierQuotes } from '../../../../quoting/client/queries/use-carrier-quotes';
import { LineOfCoverageCard } from '../../components/line-of-coverage-card';
import { useSuspenseLinesOfCoverage } from '../../queries/use-lines-of-coverage';
import type { EnrichedLoc } from '../../types';
type EnrichedLocWithCarrierQuote = EnrichedLoc & {
  carrierQuote?: CarrierQuote;
};
interface Props {
  onChange: (value: string[]) => void;
  accountUuid: string;
  initLineOfCoverageUuids?: string[];
  stage?: LineOfCoverageStageFilter;
  disabled?: boolean;
  isCompact?: boolean;
}
export function LineOfCoverageSelector(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CircleSpinnerInline />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><LineOfCoverageSelectorInner {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function LineOfCoverageSelectorInner({
  onChange,
  accountUuid,
  initLineOfCoverageUuids = [],
  stage = LineOfCoverageStageFilter.BOUNDED,
  disabled = false,
  isCompact = false
}: Props): JSX.Element {
  const {
    data: allLineOfCoverageCandidates
  } = useSuspenseLinesOfCoverage({
    accountUuid: accountUuid!,
    stage
  }, {
    // miniminze refetching when no needed
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const {
    data: carrierQuotes
  } = useSuspenseCarrierQuotes({
    accountUuid: accountUuid!,
    disabled: stage === LineOfCoverageStageFilter.BOUNDED
  }, {
    // miniminze refetching when no needed
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    gcTime: 1000 * 60 * 60,
    // 1 hour
    staleTime: 1000 * 60 * 30 // 30 minutes
  });

  // Find loc created for carrier quotes and
  const carrierQuoteLocToCarrierQuoteMap = carrierQuotes ? carrierQuotes.flatMap(carrierQuote => carrierQuote.coverageDetails.filter(coverageDetail => !!coverageDetail.uuid).map(coverageDetail_0 => [coverageDetail_0.uuid, carrierQuote])).reduce((map, [uuid, carrierQuote_0]) => map.set(uuid, carrierQuote_0), new Map()) : new Map();
  const lineOfCoverageCandidates = allLineOfCoverageCandidates?.filter(loc => stage === LineOfCoverageStageFilter.BOUNDED || loc.lineOfCoverageStatus !== 'QUOTE' || carrierQuoteLocToCarrierQuoteMap.has(loc.uuid)).map(loc_0 => ({
    ...loc_0,
    carrierQuote: carrierQuoteLocToCarrierQuoteMap.get(loc_0.uuid)
  }));

  // Initialize with preselected items if they exist
  const selectedLinesOfCoverage = lineOfCoverageCandidates.filter(loc_1 => initLineOfCoverageUuids.includes(loc_1.uuid));
  const {
    results,
    search
  } = useFuzzy(lineOfCoverageCandidates, {
    keys: ['title', 'displayName', 'policy.policyNumber', 'policy.carrier.name', 'carrierQuote.marketAccess.carrier.name', 'lineOfCoverageStatus']
  });
  const handleSelectLinesOfCoverage = (locs: EnrichedLoc[]) => {
    const newSelectedLinesOfCoverageUuids = locs.map(loc_2 => loc_2.uuid);
    onChange(newSelectedLinesOfCoverageUuids);
  };
  const lineOfCoverageSelectorRef = useRef<HTMLDivElement>(null);
  return <div ref={lineOfCoverageSelectorRef}>
      <MultiObjectSelector<EnrichedLocWithCarrierQuote> items={results} itemToString={loc_3 => loc_3?.displayName ?? ''} getItemKey={loc_4 => loc_4?.uuid ?? ''} renderItem={(loc_5, {
      isHovering,
      isSelected
    }) => <LineOfCoverageCard lineOfCoverage={loc_5} isHighlighted={isSelected} isHovering={isHovering} cardProps={{
      hideBorder: true
    }} defaultExpanded={false} />} renderSelectedItems={(loc_6, {
      remove,
      isRemovable
    }) => <LineOfCoverageCard lineOfCoverage={loc_6} actions={isRemovable ? [<IconButton key="close-button" type="close" onClick={remove} />] : []} isExpandable defaultExpanded={false} cardProps={{
      hideBorder: true,
      backgroundColor: colors.steel[50],
      padding: isCompact ? 'none' : 'default'
    }} isCompact={isCompact} />} renderItemHover={loc_7 => <div style={{
      width: 460
    }}>
            <LineOfCoverageCard defaultExpanded lineOfCoverage={loc_7} />
          </div>} onSelect={locs_0 => handleSelectLinesOfCoverage(locs_0)} selectedItems={selectedLinesOfCoverage} onSearch={search} disabled={disabled} isCompact={isCompact} popoverPortalElement={lineOfCoverageSelectorRef.current?.parentElement ?? undefined} popoverWidth="520px" />
    </div>;
}